import { z } from 'zod';
import {
    ComplianceAutomationActionType,
    ComplianceAutomationType,
} from 'src/api_openapi/v2/compliance/automationSchemas';
import {
    DepositLimitConfigurationType,
    SourceOfFundsConfigurationType,
} from 'src/api_openapi/v2/source-of-funds/sourceOfFundsSchemas';
import { SourceOfFundsState } from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFunds.state';
import {
    SourceOfFundsActionSelectionType,
    isNoneSelected,
} from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFunds.types';
import {
    DEFAULT_SOURCE_OF_FUNDS_NEW_ACCOUNT_DEPOSIT_WINDOW_DAYS,
    isSourceOfFundsCalendarPeriodValue,
} from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFundsThresholdOptions';

export const SOURCE_OF_FUNDS_THRESHOLD_AUTOMATION_NAME = '__SOF_THRESHOLD_REACHED_GLOBAL';
export const SOURCE_OF_FUNDS_NEW_ACCOUNT_AUTOMATION_NAME = '__SOF_THRESHOLD_REACHED_FRESH';

const SOURCE_OF_FUNDS_THRESHOLD_DESCRIPTION =
    'Automated action applied when an account exceeds the Source of Funds deposit threshold.';
const SOURCE_OF_FUNDS_NEW_ACCOUNT_DESCRIPTION =
    'Automated action applied when a recently opened account exceeds the Source of Funds new-account deposit threshold.';

const sourceOfFundsComplianceRestrictionConfigSchema = z.object({
    'suspension-duration': z.number(),
});

const parseWithSchema = <T>(schema: z.ZodType<T>, value: unknown): T | null => {
    const parsed = schema.safeParse(value);

    return parsed.success ? parsed.data : null;
};

const PENCE_PER_POUND = 100;

const parseAmount = (value: string): number => {
    const parsed = parseFloat(value);

    return isNaN(parsed) ? 0 : parsed;
};

const poundsToPence = (pounds: number): number => Math.round(pounds * PENCE_PER_POUND);

const penceToPoundsString = (pence: number): string => String(pence / PENCE_PER_POUND);

const parseComplianceRestrictionPeriod = (period: string): number => {
    const parsed = parseInt(period, 10);

    return parsed > 0 ? parsed : 7;
};

const SOURCE_OF_FUNDS_EXECUTION_ORDER = {
    complianceRestriction: 1,
    sendEmail: 2,
    showPopup: 3,
};

const SOURCE_OF_FUNDS_POPUP_NAME = 'sourceOfFundsRequested';
const SOURCE_OF_FUNDS_EMAIL_TEMPLATE_ID = 'source-of-funds-requested';

type SourceOfFundsCommunicationTemplatesType = {
    popupName: string;
    emailTemplateId: string;
};

const buildActionList = (
    selection: SourceOfFundsActionSelectionType,
    complianceRestrictionPeriod: string,
    templates: SourceOfFundsCommunicationTemplatesType
): ComplianceAutomationType['actions'] => {
    const actions: ComplianceAutomationType['actions'] = [];

    if (selection.complianceRestriction) {
        actions.push({
            executionOrder: SOURCE_OF_FUNDS_EXECUTION_ORDER.complianceRestriction,
            enabled: true,
            actionType: 'SetAccountStatus',
            config: {
                'status-to-set': 'compliance-restriction',
                source: 'automation',
                description: 'Source of Funds compliance restriction',
                'suspension-duration': parseComplianceRestrictionPeriod(complianceRestrictionPeriod),
            },
        });
    }

    if (selection.sendEmail) {
        actions.push({
            executionOrder: SOURCE_OF_FUNDS_EXECUTION_ORDER.sendEmail,
            enabled: true,
            actionType: 'SendEmail',
            config: {
                'template-id': templates.emailTemplateId,
            },
        });
    }

    if (selection.showPopup) {
        actions.push({
            executionOrder: SOURCE_OF_FUNDS_EXECUTION_ORDER.showPopup,
            enabled: true,
            actionType: 'ShowPopup',
            config: {
                'popup-name': templates.popupName,
            },
        });
    }

    return actions;
};

const readActionSelection = (actions: Array<ComplianceAutomationActionType>): SourceOfFundsActionSelectionType => {
    const enabledActions = actions.filter((action) => action.enabled);

    return {
        complianceRestriction: enabledActions.some((action) => action.actionType === 'SetAccountStatus'),
        sendEmail: enabledActions.some((action) => action.actionType === 'SendEmail'),
        showPopup: enabledActions.some((action) => action.actionType === 'ShowPopup'),
    };
};

const readComplianceRestrictionPeriod = (actions: Array<ComplianceAutomationActionType>): string | null => {
    const statusAction = actions.find((action) => action.enabled && action.actionType === 'SetAccountStatus');

    if (statusAction === undefined) {
        return null;
    }

    const parsedConfig = parseWithSchema(sourceOfFundsComplianceRestrictionConfigSchema, statusAction.config);

    return parsedConfig === null ? null : String(parsedConfig['suspension-duration']);
};

export const buildSourceOfFundsAutomationDtos = (
    state: SourceOfFundsState
): { threshold: ComplianceAutomationType; newAccount: ComplianceAutomationType } => {
    const threshold: ComplianceAutomationType = {
        name: SOURCE_OF_FUNDS_THRESHOLD_AUTOMATION_NAME,
        description: SOURCE_OF_FUNDS_THRESHOLD_DESCRIPTION,
        enabled: state.thresholdEnabled && !isNoneSelected(state.thresholdActions),
        actions: state.thresholdEnabled
            ? buildActionList(state.thresholdActions, state.thresholdComplianceRestrictionPeriod, {
                  popupName: SOURCE_OF_FUNDS_POPUP_NAME,
                  emailTemplateId: SOURCE_OF_FUNDS_EMAIL_TEMPLATE_ID,
              })
            : [],
    };

    const newAccount: ComplianceAutomationType = {
        name: SOURCE_OF_FUNDS_NEW_ACCOUNT_AUTOMATION_NAME,
        description: SOURCE_OF_FUNDS_NEW_ACCOUNT_DESCRIPTION,
        enabled: state.thresholdEnabled && state.newAccountEnabled && !isNoneSelected(state.newAccountActions),
        actions:
            state.thresholdEnabled && state.newAccountEnabled
                ? buildActionList(state.newAccountActions, state.newAccountComplianceRestrictionPeriod, {
                      popupName: SOURCE_OF_FUNDS_POPUP_NAME,
                      emailTemplateId: SOURCE_OF_FUNDS_EMAIL_TEMPLATE_ID,
                  })
                : [],
    };

    return { threshold, newAccount };
};

const hydrateThresholdActionsState = (state: SourceOfFundsState, automation: ComplianceAutomationType): void => {
    const selection = readActionSelection(automation.actions);

    state.setThresholdAction('complianceRestriction', selection.complianceRestriction);
    state.setThresholdAction('sendEmail', selection.sendEmail);
    state.setThresholdAction('showPopup', selection.showPopup);

    const restrictionPeriod = readComplianceRestrictionPeriod(automation.actions);

    if (restrictionPeriod !== null) {
        state.setThresholdComplianceRestrictionPeriod(restrictionPeriod);
    }
};

const hydrateNewAccountActionsState = (state: SourceOfFundsState, automation: ComplianceAutomationType): void => {
    const selection = readActionSelection(automation.actions);

    state.setNewAccountAction('complianceRestriction', selection.complianceRestriction);
    state.setNewAccountAction('sendEmail', selection.sendEmail);
    state.setNewAccountAction('showPopup', selection.showPopup);

    const restrictionPeriod = readComplianceRestrictionPeriod(automation.actions);

    if (restrictionPeriod !== null) {
        state.setNewAccountComplianceRestrictionPeriod(restrictionPeriod);
    }
};

export const hydrateSourceOfFundsStateFromAutomations = (
    state: SourceOfFundsState,
    threshold: ComplianceAutomationType | null,
    newAccount: ComplianceAutomationType | null
): void => {
    if (threshold !== null) {
        hydrateThresholdActionsState(state, threshold);
    }

    if (newAccount !== null) {
        hydrateNewAccountActionsState(state, newAccount);
    }
};

export const buildSourceOfFundsConfigDto = (state: SourceOfFundsState): SourceOfFundsConfigurationType => {
    const globalLimit: DepositLimitConfigurationType = {
        type: state.thresholdPeriodType,
        calc: state.thresholdCalculationType,
        window: parseAmount(
            state.thresholdPeriodType === 'rolling'
                ? state.thresholdRollingPeriodDays
                : state.thresholdCalendarPeriodValue
        ),
        threshold: poundsToPence(parseAmount(state.thresholdAmount)),
    };

    const freshCustomerThreshold = poundsToPence(parseAmount(state.newAccountAmount));
    const depositWindowDays = parseAmount(state.newAccountDepositWindowDays);
    const defaultDepositWindowDays = parseAmount(DEFAULT_SOURCE_OF_FUNDS_NEW_ACCOUNT_DEPOSIT_WINDOW_DAYS);

    const freshCustomerLimit: DepositLimitConfigurationType = {
        type: 'rolling',
        calc: state.newAccountCalculationType,
        window: depositWindowDays > 0 ? depositWindowDays : defaultDepositWindowDays,
        threshold: freshCustomerThreshold > 0 ? freshCustomerThreshold : 1,
    };

    const daysSinceSignup = parseAmount(state.newAccountDaysSinceSignup);

    return {
        globalLimit,
        freshCustomerSignupWindow:
            state.thresholdEnabled && state.newAccountEnabled ? (daysSinceSignup > 0 ? daysSinceSignup : 1) : 0,
        freshCustomerLimit,
    };
};

export const hydrateSourceOfFundsStateFromConfig = (
    state: SourceOfFundsState,
    config: SourceOfFundsConfigurationType
): void => {
    state.setThresholdAmount(penceToPoundsString(config.globalLimit.threshold));
    state.setThresholdCalculationType(config.globalLimit.calc);

    if (config.globalLimit.type === 'calendar') {
        state.setThresholdPeriodType('calendar');

        const windowValue = String(config.globalLimit.window);

        if (isSourceOfFundsCalendarPeriodValue(windowValue)) {
            state.setThresholdCalendarPeriodValue(windowValue);
        }
    } else {
        state.setThresholdPeriodType('rolling');
        state.setThresholdRollingPeriodDays(String(config.globalLimit.window));
    }

    const enabled = config.freshCustomerSignupWindow > 0;

    state.setNewAccountEnabled(enabled);
    state.setNewAccountAmount(penceToPoundsString(config.freshCustomerLimit.threshold));
    state.setNewAccountCalculationType(config.freshCustomerLimit.calc);
    state.setNewAccountDepositWindowDays(String(config.freshCustomerLimit.window));

    if (enabled) {
        state.setNewAccountDaysSinceSignup(String(config.freshCustomerSignupWindow));
    }
};
